import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, ChartBar, ChartLine, Table as TableIcon,
  FilePdf, CalendarBlank, ArrowsHorizontal, Calendar,
} from "@phosphor-icons/react";

const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const YEAR_COLORS = ["#059669", "#1e40af", "#dc2626", "#a855f7", "#f59e0b", "#0891b2"];
const currentYear = new Date().getFullYear();

export default function ContractStats() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mode, setMode] = useState("years"); // "years" | "range"
  const [years, setYears] = useState([currentYear, currentYear - 1]);
  const [dateFrom, setDateFrom] = useState(`${currentYear - 1}-01-01`);
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [viewMode, setViewMode] = useState("bars"); // "bars" | "lines" | "matrix"
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    if (mode === "range") return { mode: "range", date_from: dateFrom, date_to: dateTo };
    return { mode: "years", years: years.join(",") };
  }, [mode, years, dateFrom, dateTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/contracts/${id}/range-stats`, { params });
        setStats(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Impossible de charger les statistiques"); }
      finally { setLoading(false); }
    })();
  }, [id, params]);

  const toggleYear = (y) => {
    setYears((prev) => prev.includes(y)
      ? (prev.length > 1 ? prev.filter((x) => x !== y) : prev)
      : [...prev, y].sort());
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/contracts/${id}/range-stats.pdf`, {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la génération du PDF"); }
  };

  if (loading) return <div className="text-muted-foreground p-6">Chargement…</div>;
  if (!stats) return null;

  const byYear = stats.by_year || [];
  const annualQuota = stats.contract.annual_hours || (stats.contract.monthly_hours || 0) * 12;
  const monthlyPackage = stats.contract.monthly_package || 0;
  const overrunRate = stats.contract.overrun_rate || stats.contract.hourly_rate || 0;
  const quota = stats.contract.monthly_hours || (annualQuota / 12); // monthly for chart quota line
  const rate = overrunRate;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/contrats")} className="-ml-2" data-testid="back-contracts">
        <ArrowLeft size={14} className="mr-1.5"/> Retour aux contrats
      </Button>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps">Bilan de consommation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="contract-name">{stats.contract.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            Client : <b>{stats.contract.client_name || "—"}</b> · Forfait annuel : <b>{annualQuota}h</b> · Forfait mensuel : <b>{monthlyPackage.toFixed(2)}€/mois</b> · Dépassement : <b>{overrunRate}€/h</b>
          </div>
          <div className="text-xs text-muted-foreground mt-1 font-mono" data-testid="period-label">{stats.period_label}</div>
        </div>
        <Button onClick={downloadPdf} data-testid="download-range-pdf">
          <FilePdf size={16} className="mr-2"/> Exporter le PDF
        </Button>
      </header>

      {/* OVERRUN BANNER — very prominent */}
      {stats.total_overrun_hours > 0 && (
        <div className="rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white p-4 shadow-md" data-testid="overrun-banner">
          <div className="flex items-center gap-3">
            <div className="text-3xl">⚠</div>
            <div className="flex-1">
              <div className="font-display text-lg font-bold">Dépassement de forfait détecté</div>
              <div className="text-sm opacity-95 mt-0.5">
                <b>{stats.total_overrun_hours}h</b> hors forfait sur la période ·
                Coût à facturer : <b>{stats.total_overrun_cost.toFixed(2)}€ HT</b>
                ({overrunRate}€/h × {stats.total_overrun_hours}h)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Period selector card */}
      <Card><CardContent className="pt-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="label-caps mr-2">Période</div>
          <div className="inline-flex rounded-md border p-1 bg-muted/40">
            <Button size="sm" variant={mode === "years" ? "default" : "ghost"} className="h-7 px-3"
              onClick={() => setMode("years")} data-testid="mode-years">
              <Calendar size={13} className="mr-1.5"/> Année(s)
            </Button>
            <Button size="sm" variant={mode === "range" ? "default" : "ghost"} className="h-7 px-3"
              onClick={() => setMode("range")} data-testid="mode-range">
              <ArrowsHorizontal size={13} className="mr-1.5"/> Plage libre
            </Button>
          </div>
        </div>

        {mode === "years" && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="years-picker">
            {[0, 1, 2, 3, 4, 5].map((offset) => {
              const y = currentYear - offset;
              const active = years.includes(y);
              return (
                <button
                  key={y}
                  onClick={() => toggleYear(y)}
                  data-testid={`year-toggle-${y}`}
                  className={`px-3 py-1.5 rounded-md border text-sm font-medium transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "bg-background hover:bg-muted border-border"
                  }`}
                >{y}</button>
              );
            })}
            <span className="text-xs text-muted-foreground ml-2">{years.length} année{years.length > 1 ? "s" : ""} sélectionnée{years.length > 1 ? "s" : ""}</span>
          </div>
        )}

        {mode === "range" && (
          <div className="flex items-end gap-3 flex-wrap" data-testid="range-picker">
            <div>
              <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Du</div>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9" data-testid="date-from"/>
            </div>
            <div>
              <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Au</div>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9" data-testid="date-to"/>
            </div>
            <span className="text-xs text-muted-foreground mb-2">
              Couvre {byYear.length} année{byYear.length > 1 ? "s" : ""} calendaire{byYear.length > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </CardContent></Card>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Heures consommées" value={`${stats.total_hours}h`} tone="primary" testid="kpi-hours"/>
        <Kpi label="Tickets" value={stats.total_tickets} tone="muted" testid="kpi-tickets"/>
        <Kpi label="Dépassement" value={`${stats.total_overrun_hours}h`}
          tone={stats.total_overrun_hours > 0 ? "danger" : "success"} testid="kpi-overrun"/>
        <Kpi label="Coût dépassement" value={`${stats.total_overrun_cost.toFixed(2)}€`}
          tone={stats.total_overrun_cost > 0 ? "danger" : "success"} testid="kpi-cost"/>
      </div>

      {/* Multi-year comparison summary (if >=2 years) */}
      {byYear.length >= 2 && <YearSummaryTable byYear={byYear} rate={rate}/>}

      {/* Chart / matrix section */}
      <Card><CardContent className="pt-6">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <ChartBar size={18} className="text-primary"/>
            <h2 className="font-display font-semibold text-lg">Évolution mensuelle</h2>
          </div>
          <div className="inline-flex rounded-md border p-1 bg-muted/40">
            <Button size="sm" variant={viewMode === "bars" ? "default" : "ghost"} className="h-7 px-2.5"
              onClick={() => setViewMode("bars")} data-testid="view-bars">
              <ChartBar size={13} className="mr-1"/> Barres
            </Button>
            <Button size="sm" variant={viewMode === "lines" ? "default" : "ghost"} className="h-7 px-2.5"
              onClick={() => setViewMode("lines")} data-testid="view-lines">
              <ChartLine size={13} className="mr-1"/> Courbes
            </Button>
            <Button size="sm" variant={viewMode === "matrix" ? "default" : "ghost"} className="h-7 px-2.5"
              onClick={() => setViewMode("matrix")} data-testid="view-matrix">
              <TableIcon size={13} className="mr-1"/> Matrice
            </Button>
          </div>
        </div>

        {viewMode === "bars" && <GroupedBarsChart byYear={byYear} quota={quota}/>}
        {viewMode === "lines" && <LinesChart byYear={byYear} quota={quota}/>}
        {viewMode === "matrix" && <MatrixTable byYear={byYear} quota={quota} rate={rate}/>}
      </CardContent></Card>

      {/* Tickets list */}
      {(stats.tickets && stats.tickets.length > 0) && (
        <Card><CardContent className="pt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-lg">
              Tickets sur la période <span className="text-muted-foreground text-sm">({stats.tickets.length}{stats.tickets_capped ? "+" : ""})</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2 px-2">N°</th>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Sujet</th>
                  <th className="text-left py-2 px-2">Priorité</th>
                  <th className="text-left py-2 px-2">Statut</th>
                  <th className="text-right py-2 px-2">Temps</th>
                </tr>
              </thead>
              <tbody data-testid="tickets-list">
                {stats.tickets.map((t) => (
                  <tr key={t.number} className="border-b hover:bg-muted/40" data-testid={`ticket-row-${t.number}`}>
                    <td className="py-2 px-2 mono font-medium">#{t.number}</td>
                    <td className="py-2 px-2 text-xs text-muted-foreground whitespace-nowrap">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 px-2">{t.subject}</td>
                    <td className="py-2 px-2 text-xs">{(t.priority || "").replace(/^./, (c) => c.toUpperCase())}</td>
                    <td className="py-2 px-2 text-xs">{(t.status || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}</td>
                    <td className="py-2 px-2 text-right mono">{((t.time_spent_minutes || 0) / 60).toFixed(2)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

// -------- Sub-components --------------------------------------------------

function YearSummaryTable({ byYear, rate }) {
  return (
    <Card><CardContent className="pt-6">
      <h2 className="font-display font-semibold text-lg mb-3">Comparatif annuel</h2>
      <table className="w-full text-sm" data-testid="year-summary-table">
        <thead className="text-xs uppercase text-muted-foreground border-b">
          <tr>
            <th className="text-left py-2 px-2">Année</th>
            <th className="text-right py-2 px-2">Tickets</th>
            <th className="text-right py-2 px-2">Heures</th>
            <th className="text-right py-2 px-2">Dépass. (h)</th>
            <th className="text-right py-2 px-2">Coût dép.</th>
            <th className="text-right py-2 px-2">Δ vs N-1</th>
          </tr>
        </thead>
        <tbody>
          {byYear.map((y, i) => {
            const prev = i > 0 ? byYear[i - 1] : null;
            const delta = prev && prev.total_hours > 0
              ? ((y.total_hours - prev.total_hours) / prev.total_hours) * 100
              : null;
            return (
              <tr key={y.year} className="border-b" data-testid={`year-row-${y.year}`}>
                <td className="py-2 px-2 font-semibold">
                  <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle"
                    style={{ background: YEAR_COLORS[i % YEAR_COLORS.length] }}/>
                  {y.year}
                </td>
                <td className="py-2 px-2 text-right">{y.total_tickets}</td>
                <td className="py-2 px-2 text-right font-medium">{y.total_hours}h</td>
                <td className={`py-2 px-2 text-right ${y.total_overrun_hours > 0 ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                  {y.total_overrun_hours}h
                </td>
                <td className={`py-2 px-2 text-right ${y.total_overrun_cost > 0 ? "text-red-600" : "text-muted-foreground"}`}>
                  {y.total_overrun_cost.toFixed(2)}€
                </td>
                <td className={`py-2 px-2 text-right ${delta === null ? "text-muted-foreground" : delta >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {delta === null ? "—" : `${delta >= 0 ? "▲" : "▼"} ${delta.toFixed(1).replace("-", "")}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function GroupedBarsChart({ byYear, quota }) {
  const maxHours = Math.max(
    quota * 1.2,
    ...byYear.flatMap((y) => y.monthly.map((m) => m.hours)),
    1,
  );
  const barWidth = Math.max(2, Math.floor(20 / Math.max(1, byYear.length)));
  return (
    <div>
      <div className="flex items-end gap-2 h-64 border-b border-l relative pl-10 pb-6">
        {quota > 0 && (
          <div className="absolute left-10 right-0 border-t-2 border-dashed border-orange-400 pointer-events-none"
               style={{ bottom: `${24 + (quota / maxHours) * (100 - 12)}%`, top: "auto" }}>
            <span className="absolute -top-4 right-0 text-[10px] text-orange-600 font-semibold bg-background px-1">
              Quota {quota}h
            </span>
          </div>
        )}
        {MONTH_LABELS.map((label, monthIdx) => (
          <div key={label} className="flex-1 flex flex-col items-center gap-1" data-testid={`bars-month-${monthIdx + 1}`}>
            <div className="w-full flex justify-center items-end gap-[2px] h-full">
              {byYear.map((y, i) => {
                const entry = y.monthly[monthIdx];
                const h = (entry.hours / maxHours) * 100;
                return (
                  <div key={y.year}
                    title={`${y.year} — ${MONTH_LABELS[monthIdx]}: ${entry.hours}h${entry.overrun > 0 ? ` (+${entry.overrun}h)` : ""}`}
                    className="rounded-t transition-all"
                    style={{
                      height: `${h}%`,
                      width: `${barWidth}px`,
                      background: entry.overrun > 0 ? "#dc2626" : YEAR_COLORS[i % YEAR_COLORS.length],
                    }}/>
                );
              })}
            </div>
            <div className="text-[10px] text-muted-foreground uppercase font-mono">{label}</div>
          </div>
        ))}
      </div>
      <LegendYears byYear={byYear}/>
    </div>
  );
}

function LinesChart({ byYear, quota }) {
  const width = 720, height = 240, pad = { l: 40, r: 20, t: 20, b: 24 };
  const innerW = width - pad.l - pad.r, innerH = height - pad.t - pad.b;
  const maxHours = Math.max(
    quota * 1.2,
    ...byYear.flatMap((y) => y.monthly.map((m) => m.hours)),
    1,
  );
  const xFor = (m) => pad.l + ((m - 1) / 11) * innerW;
  const yFor = (h) => pad.t + innerH - (h / maxHours) * innerH;
  const quotaY = yFor(quota);

  return (
    <div>
      <div className="overflow-x-auto">
        <svg width={width} height={height} className="max-w-full h-auto" data-testid="lines-chart">
          {/* Grid */}
          {[0.25, 0.5, 0.75, 1].map((f) => (
            <line key={f} x1={pad.l} x2={width - pad.r}
              y1={pad.t + innerH - f * innerH} y2={pad.t + innerH - f * innerH}
              stroke="#e2e8f0" strokeDasharray="2,2"/>
          ))}
          {/* Axes */}
          <line x1={pad.l} y1={pad.t} x2={pad.l} y2={height - pad.b} stroke="#94a3b8"/>
          <line x1={pad.l} y1={height - pad.b} x2={width - pad.r} y2={height - pad.b} stroke="#94a3b8"/>
          {/* Quota line */}
          {quota > 0 && (
            <>
              <line x1={pad.l} x2={width - pad.r} y1={quotaY} y2={quotaY} stroke="#f97316" strokeDasharray="4,4"/>
              <text x={width - pad.r} y={quotaY - 4} textAnchor="end" fontSize={10} fill="#ea580c" fontWeight={600}>
                Quota {quota}h
              </text>
            </>
          )}
          {/* Month labels */}
          {MONTH_LABELS.map((label, i) => (
            <text key={label} x={xFor(i + 1)} y={height - 6} textAnchor="middle" fontSize={10} fill="#64748b">{label}</text>
          ))}
          {/* Y ticks */}
          {[0, 0.5, 1].map((f) => {
            const h = f * maxHours;
            return (
              <text key={f} x={pad.l - 6} y={yFor(h) + 3} textAnchor="end" fontSize={9} fill="#64748b">
                {h.toFixed(0)}h
              </text>
            );
          })}
          {/* Lines per year */}
          {byYear.map((y, i) => {
            const color = YEAR_COLORS[i % YEAR_COLORS.length];
            const points = y.monthly.map((m) => `${xFor(m.month)},${yFor(m.hours)}`).join(" ");
            return (
              <g key={y.year}>
                <polyline points={points} fill="none" stroke={color} strokeWidth={2}
                  strokeLinejoin="round" strokeLinecap="round"/>
                {y.monthly.map((m) => (
                  <circle key={m.month} cx={xFor(m.month)} cy={yFor(m.hours)} r={3} fill={color}>
                    <title>{`${y.year} — ${MONTH_LABELS[m.month - 1]}: ${m.hours}h`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      <LegendYears byYear={byYear}/>
    </div>
  );
}

function MatrixTable({ byYear, quota, rate }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="matrix-table">
        <thead className="text-xs uppercase text-muted-foreground border-b">
          <tr>
            <th className="text-left py-2 px-2">Mois</th>
            {byYear.map((y) => (
              <th key={y.year} className="text-right py-2 px-2 whitespace-nowrap">{y.year}</th>
            ))}
            <th className="text-right py-2 px-2 text-primary">Total</th>
          </tr>
        </thead>
        <tbody>
          {MONTH_LABELS.map((label, mi) => {
            const rowSum = byYear.reduce((acc, y) => acc + y.monthly[mi].hours, 0);
            return (
              <tr key={label} className="border-b" data-testid={`matrix-row-${mi + 1}`}>
                <td className="py-1.5 px-2 font-medium">{label}</td>
                {byYear.map((y) => {
                  const entry = y.monthly[mi];
                  return (
                    <td key={y.year}
                      className={`py-1.5 px-2 text-right mono ${entry.overrun > 0 ? "text-red-600 font-semibold" : ""}`}
                      title={`${entry.tickets} ticket(s) · quota ${quota}h${entry.overrun > 0 ? ` · +${entry.overrun}h dépassement (${entry.overrun_cost.toFixed(2)}€)` : ""}`}
                    >
                      {entry.hours}h
                    </td>
                  );
                })}
                <td className="py-1.5 px-2 text-right mono font-semibold text-primary">{rowSum.toFixed(2)}h</td>
              </tr>
            );
          })}
          {/* Totals row */}
          <tr className="border-t-2 bg-muted/40 font-semibold">
            <td className="py-2 px-2">TOTAL</td>
            {byYear.map((y) => (
              <td key={y.year} className="py-2 px-2 text-right mono">{y.total_hours}h</td>
            ))}
            <td className="py-2 px-2 text-right mono text-primary">
              {byYear.reduce((acc, y) => acc + y.total_hours, 0).toFixed(2)}h
            </td>
          </tr>
          {/* Overrun row */}
          <tr className="bg-red-50/50 dark:bg-red-950/20 text-xs">
            <td className="py-1.5 px-2 text-red-700 dark:text-red-400">Coût dépassement</td>
            {byYear.map((y) => (
              <td key={y.year} className="py-1.5 px-2 text-right mono text-red-700 dark:text-red-400">
                {y.total_overrun_cost.toFixed(2)}€
              </td>
            ))}
            <td className="py-1.5 px-2 text-right mono text-red-700 dark:text-red-400">
              {byYear.reduce((acc, y) => acc + y.total_overrun_cost, 0).toFixed(2)}€
            </td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground mt-2">
        Quota mensuel : <b>{quota}h</b> · Tarif : <b>{rate}€/h</b> · Les valeurs en rouge dépassent le quota mensuel.
      </p>
    </div>
  );
}

function LegendYears({ byYear }) {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3 flex-wrap">
      {byYear.map((y, i) => (
        <span key={y.year} className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded inline-block" style={{ background: YEAR_COLORS[i % YEAR_COLORS.length] }}/>
          {y.year} · {y.total_hours}h
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded bg-red-500 inline-block"/> Mois en dépassement
      </span>
    </div>
  );
}

function Kpi({ label, value, hint, tone = "primary", testid }) {
  const toneCls = {
    primary: "bg-primary/5 border-primary/20 text-primary",
    danger: "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/30 dark:text-red-300",
    warning: "bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300",
    success: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
    muted: "bg-muted text-muted-foreground border",
  }[tone];
  return (
    <Card><CardContent className="pt-6" data-testid={testid}>
      <div className={`text-[0.7rem] uppercase tracking-wider ${toneCls} inline-block px-2 py-0.5 rounded-full border`}>{label}</div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </CardContent></Card>
  );
}
